// @ts-nocheck
import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ChevronRight,
  CheckCircle,
  XCircle,
  AlertCircle,
  Building2,
  MapPin,
  FileText,
  Users,
  MessageSquare,
  ShieldCheck,
  Clock,
  Loader2,
  ArrowLeft,
  X,
  ExternalLink,
  Layers,
  Eye,
  Contact,
  Home,
  DollarSign,
  User,
  Zap,
  RotateCcw,
  Activity,
  ShieldAlert,
  Search,
  FileSignature,
  Send,
  RefreshCw,
  Lock,
  Save,
  Percent,
  CreditCard,
  Scale
} from "lucide-react";

import { doc, collection, query, where, updateDoc, onSnapshot, arrayUnion, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../../config/firebase";
import { useAuth } from "../../contexts/AuthContext";
import { enviarSolicitacaoFC, consultarLaudoFC, getLinkLaudoPDF, cadastrarWebhookFC } from "../../services/fcService";

// ============================================================================
// 🚀 COMPONENTES BASE
// ============================================================================

const DataField = ({ label, value, className = "" }) => (
  <div className={className}>
    <p className="text-xs font-medium text-slate-500 mb-1">{label}</p>
    <p className="text-sm font-semibold text-slate-800">{value || "-"}</p>
  </div>
);

// Máscara de Moeda
const maskCurrency = (value) => {
  if (!value) return "";
  let v = value.replace(/\D/g, "");
  if (v === "") return "";
  v = (Number(v) / 100).toFixed(2) + "";
  v = v.replace(".", ",");
  v = v.replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1.");
  return v;
};

// Parsing Seguro de Valores Financeiros (Trata ponto e vírgula corretamente)
const parseCurrencyToFloat = (value) => {
  if (!value) return 0;
  let str = String(value);
  // Se tiver vírgula, assumimos que é padrão PT-BR (ex: 1.234,56 ou 12,34)
  if (str.includes(',')) {
    str = str.replace(/\./g, '').replace(',', '.');
  }
  return parseFloat(str) || 0;
};

// Soma de Valores Monetários em String
const calcularSoma = (val1, val2) => {
  const sum = parseCurrencyToFloat(val1) + parseCurrencyToFloat(val2);
  return sum.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// ============================================================================
// 🚀 MESA DE OPERAÇÕES ADMIN (ANALISE DA FICHA)
// ============================================================================

const AdminAnaliseSolicitacaoPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userInfo } = useAuth();

  const protocoloReal = id ? id.replace('-', '/') : "";

  // ----------------------------------------------------------------------------
  // ⚠️ BLOCO DE ESTADOS
  // ----------------------------------------------------------------------------
  const [solicitacao, setSolicitacao] = useState(null);
  const [partes, setPartes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [docRefId, setDocRefId] = useState(null);

  const [modelosParecer, setModelosParecer] = useState([]);

  const [fcResults, setFcResults] = useState({});

  // eslint-disable-next-line no-unused-vars
  const [docValidations, setDocValidations] = useState({});

  const [contractStatus, setContractStatus] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [parecerType, setParecerType] = useState("");
  const [parecerText, setParecerText] = useState("");

  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [parteSelecionada, setParteSelecionada] = useState(null);

  const [isDataModalOpen, setIsDataModalOpen] = useState(false);
  const [parteDadosSelecionada, setParteDadosSelecionada] = useState(null);

  const [isSignedDocsModalOpen, setIsSignedDocsModalOpen] = useState(false);

  const [condicoesAprovacao, setCondicoesAprovacao] = useState({
    percentual: "",
    valorTaxa: "",
    formaPagamento: ""
  });
  const [isCondicoesSaved, setIsCondicoesSaved] = useState(false);
  const [isSavingCondicoes, setIsSavingCondicoes] = useState(false);

  // ============================================================================
  // 1. EFEITOS E BUSCAS INICIAIS
  // ============================================================================
  useEffect(() => {
    if (isModalOpen || isDocModalOpen || isDataModalOpen || isSignedDocsModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isModalOpen, isDocModalOpen, isDataModalOpen, isSignedDocsModalOpen]);

  useEffect(() => {
    if (!protocoloReal) return;
    setLoading(true);

    const q = query(collection(db, "requests"), where("protocolo", "==", protocoloReal));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        const docData = snapshot.docs[0];
        const data = docData.data();

        setDocRefId(docData.id);
        setSolicitacao({ id: docData.id, ...data });

        if (data.contractStatus) {
          setContractStatus(data.contractStatus);
        } else if (data.status === 'Garantia Ativa') {
          setContractStatus('closed');
        }

        if (data.condicoesAprovacao) {
          setCondicoesAprovacao({
            percentual: data.condicoesAprovacao.percentual || "",
            valorTaxa: data.condicoesAprovacao.valorTaxa || "",
            formaPagamento: data.condicoesAprovacao.formaPagamento || ""
          });
          if (data.condicoesAprovacao.percentual && data.condicoesAprovacao.valorTaxa && data.condicoesAprovacao.formaPagamento) {
            setIsCondicoesSaved(true);
          }
        }

      } else {
        setSolicitacao(null);
      }
      setLoading(false);
    }, (error) => {
      console.error("Erro ao buscar detalhes:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [protocoloReal]);

  useEffect(() => {
    if (!docRefId) return;

    const partesRef = collection(db, `requests/${docRefId}/partes`);

    const unsubscribePartes = onSnapshot(partesRef, (partesSnapshot) => {
      const partesData = partesSnapshot.docs.map(pDoc => ({ id: pDoc.id, ...pDoc.data() }));
      setPartes(partesData);

      const updatedResults = {};
      partesData.forEach(p => {
        if (p.fcSolicitationId) {
          const dataResult = p.fcLastResult || {
            score: p.fcScore || 'N/A',
            risco: p.fcRisco || 'EM ANÁLISE',
            cor: p.fcCor || 'yellow',
            fcId: p.fcSolicitationId
          };

          updatedResults[p.id] = {
            loading: false,
            data: dataResult
          };
        }
      });
      setFcResults(updatedResults);
    }, (err) => {
      console.warn("Aviso: Falha ao escutar sub-coleção.", err);
    });

    return () => unsubscribePartes();
  }, [docRefId]);

  useEffect(() => {
    const q = query(collection(db, "parecer_templates"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const modelosData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          nome: data.nome || "",
          titulo: data.titulo || "",
          descricao: data.descricao || "",
          ...data
        };
      });
      modelosData.sort((a, b) => a.nome.localeCompare(b.nome));
      setModelosParecer(modelosData);
    }, (error) => {
      console.error("Aviso: Falha ao carregar modelos de parecer.", error);
    });

    return () => unsubscribe();
  }, []);

  // ============================================================================
  // 2. SALVAR CONDIÇÕES DE APROVAÇÃO
  // ============================================================================
  const handleSaveCondicoes = async () => {
    if (!condicoesAprovacao.percentual || !condicoesAprovacao.valorTaxa || !condicoesAprovacao.formaPagamento) {
      alert("Por favor, preencha todos os campos (Taxa, Valor e Forma de Pagamento) antes de salvar.");
      return;
    }

    setIsSavingCondicoes(true);
    try {
      const docRef = doc(db, "requests", docRefId);
      await updateDoc(docRef, {
        condicoesAprovacao: {
          percentual: condicoesAprovacao.percentual,
          valorTaxa: condicoesAprovacao.valorTaxa,
          formaPagamento: condicoesAprovacao.formaPagamento
        }
      });

      setIsCondicoesSaved(true);
      setTimeout(() => setIsSavingCondicoes(false), 500);

    } catch (error) {
      console.error("Erro ao salvar condições:", error);
      alert("Erro ao salvar as condições. Tente novamente.");
      setIsSavingCondicoes(false);
    }
  };

  // ============================================================================
  // 3. AÇÕES DE STATUS E LIBERAÇÃO DE ASSINATURA
  // ============================================================================
  const handleAprovarDireto = async () => {
    if (isUpdating) return; // Trava contra duplo-clique imediata
    if (!docRefId) return;

    if (!isCondicoesSaved) {
      alert("Você precisa salvar as Condições de Aprovação no card lateral antes de aprovar a ficha.");
      return;
    }

    // ============================================================================
    // FASE 5: BYPASS DUPLO PARA CADASTROS LEGADOS
    // ============================================================================
    if (solicitacao.isLegacy) {
      const confirmarLegacy = window.confirm("ATENÇÃO: Esta é uma solicitação LEGADA.\n\nDeseja aprovar e pular direto para GARANTIA ATIVA (Bypass FC e ClickSign)?");
      if (!confirmarLegacy) return;
      
      setIsUpdating(true);
      try {
        const docRef = doc(db, "requests", docRefId);
        const contratoAtivoRef = doc(db, "contratos_ativos", docRefId);
        
        // Capturar locatário para o faturamento (Pagador)
        const locatario = partes.find(p => p.dadosPessoais?.pretendenteA === "Locatário") || partes[0];

        // 1. Atualizar a Solicitação original (Bypass status e history)
        await updateDoc(docRef, { 
          contractStatus: 'closed',
          status: 'Garantia Ativa',
          updatedAt: serverTimestamp(),
          historico: arrayUnion({
            data: new Date().toISOString(),
            status: "Garantia Ativa",
            mensagem: "Contrato legado importado e ativado diretamente (Bypass FC Análises e ClickSign).",
            autor: userInfo?.name || "Diretoria Real Benefícios"
          })
        });
        
        // 2. Injetar na Coleção de Contratos Ativos para Motor de Faturamento
        await setDoc(contratoAtivoRef, {
          requestId: docRefId,
          protocoloOrigem: solicitacao.protocolo,
          companyId: solicitacao.companyId,
          companyName: solicitacao.companyName,
          dataAtivacao: serverTimestamp(),
          status: "Contrato Ativo",
          isLegacy: true,
          imovel: solicitacao.imovel,
          pagador: {
            nome: locatario?.dadosPessoais?.nome || "Não informado",
            cpf: locatario?.dadosPessoais?.cpf || "Não informado",
            email: locatario?.dadosPessoais?.email || "",
            telefone: locatario?.dadosPessoais?.telefone || ""
          },
          faturamento: {
            valorTaxa: condicoesAprovacao.valorTaxa,
            formaPagamento: condicoesAprovacao.formaPagamento,
            percentual: condicoesAprovacao.percentual
          },
          // INJEÇÃO FASE 6: Carimbo de Ciclo Financeiro (B2B)
          ciclo: {
            statusRepasse: condicoesAprovacao.formaPagamento === "Boleto Imobiliária" 
              ? "Aguardando Fechamento" 
              : "Isolado - Pagamento Único"
          }
        });

        setContractStatus('closed');
        setSolicitacao(prev => ({ ...prev, status: 'Garantia Ativa' }));
        alert("✅ Sucesso! Contrato legado ativado e ejetado no fluxo de faturamento.");
      } catch (error) {
        console.error("Erro no Bypass Legado:", error);
        alert("❌ Erro ao ativar contrato legado: " + error.message);
      } finally {
        setIsUpdating(false);
      }
      return;
    }

    // ============================================================================
    // FLUXO NORMAL DE APROVAÇÃO
    // ============================================================================
    const confirmar = window.confirm(`Deseja realmente APROVAR o cadastro? A imobiliária será notificada.`);
    if (!confirmar) return;
    executarMudancaStatus('Aprovada', 'Cadastro aprovado com sucesso.');
  };

  const handleRevogar = async (statusAtual) => {
    if (isUpdating) return; // Trava contra duplo-clique imediata
    if (!docRefId) return;
    
    const confirmar = window.confirm(`Atenção: Deseja realmente revogar o status de "${statusAtual}"? A ficha retornará para "Em Análise". O progresso de contrato e faturamento será zerado.`);
    if (!confirmar) return;

    const docRef = doc(db, "requests", docRefId);
    await updateDoc(docRef, { contractStatus: null, paymentStatus: null });
    setContractStatus(null);

    executarMudancaStatus('Em Análise', `Status anterior (${statusAtual}) revogado. Ficha reaberta para análise.`);
  };

  const executarMudancaStatus = async (novoStatus, mensagemParecer) => {
    setIsUpdating(true);
    try {
      const docRef = doc(db, "requests", docRefId);
      const novoHistorico = {
        data: new Date().toISOString(),
        status: novoStatus,
        mensagem: mensagemParecer,
        autor: userInfo?.name || "SuperAdmin"
      };

      await updateDoc(docRef, {
        status: novoStatus,
        updatedAt: serverTimestamp(),
        historico: arrayUnion(novoHistorico)
      });

    } catch (error) {
      console.error("Erro ao atualizar status:", error);
      alert("Falha ao atualizar o status. Verifique o console.");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleLiberarParaAssinaturas = async () => {
    if (isUpdating) return; // Trava contra duplo-clique (evita chamar o motor 2x)

    const confirmar = window.confirm("Deseja liberar a ficha para a Imobiliária? O Motor ClickSign utilizará os modelos de Carta Fiança e Termo de Garantia automaticamente na próxima etapa.");
    if (!confirmar) return;

    setIsUpdating(true);
    try {
      const docRef = doc(db, "requests", docRefId);

      await updateDoc(docRef, {
        contractStatus: 'draft',
        updatedAt: serverTimestamp(),
        historico: arrayUnion({
          data: new Date().toISOString(),
          status: "Aguardando Assinaturas",
          mensagem: "Ficha liberada. Modelos ClickSign configurados e prontos para uso pela Imobiliária.",
          autor: userInfo?.name || "SuperAdmin"
        })
      });

      setContractStatus('draft');
    } catch (error) {
      console.error("Erro ao liberar para assinaturas:", error);
      alert("Erro ao liberar ficha. Verifique sua conexão e tente novamente.");
    } finally {
      setIsUpdating(false);
    }
  };

  // ============================================================================
  // 4. INTEGRAÇÃO REAL: FC ANÁLISES E ASAAS (MOCK)
  // ============================================================================

  const handleCadastrarWebhook = async () => {
    try {
      setIsUpdating(true);
      await cadastrarWebhookFC();
      alert("✅ Webhook cadastrado com sucesso na FC Análises!");
    } catch (error) {
      console.error("Erro ao cadastrar webhook:", error);
      alert("❌ Erro ao cadastrar webhook: " + error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const executarAnaliseFCIndividual = async (parte) => {
    setFcResults(prev => ({ ...prev, [parte.id]: { loading: true, data: null } }));

    try {
      const result = await enviarSolicitacaoFC(solicitacao, parte);
      const fcId = result.id;

      const parteRef = doc(db, `requests/${docRefId}/partes`, parte.id);

      const defaultStatus = { score: 'N/A', risco: 'EM ANÁLISE', cor: 'yellow', fcId: fcId, rawData: result };

      await updateDoc(parteRef, {
        fcSolicitationId: fcId,
        fcStatus: 'SOLICITADO',
        fcLastResult: defaultStatus,
        lastFcSync: new Date().toISOString()
      });

    } catch (error) {
      console.error("Erro na integração FC:", error);
      alert("Erro ao enviar para FC Análises:\n\n" + (error.message || "Erro desconhecido."));
      setFcResults(prev => ({ ...prev, [parte.id]: { loading: false, data: null } }));
    }
  };

  const atualizarStatusFC = async (parteId) => {
    const fcId = fcResults[parteId]?.data?.fcId;
    if (!fcId) return;

    setFcResults(prev => ({ ...prev, [parteId]: { ...prev[parteId], loading: true } }));

    try {
      const details = await consultarLaudoFC(fcId);

      if (details?.message === "Esta Solicitação não possui laudos concluídos.") {
        const processandoResult = {
          score: "N/A",
          risco: "PROCESSANDO",
          cor: "yellow",
          fcId: fcId,
          rawData: details
        };

        setFcResults(prev => ({ ...prev, [parteId]: { loading: false, data: processandoResult } }));

        const parteRef = doc(db, `requests/${docRefId}/partes`, parteId);
        await updateDoc(parteRef, { fcLastResult: processandoResult });

        alert("A FC Análises ainda está processando este laudo na fila (Sandbox). O status atualizará via Webhook em breve.");
        return;
      }

      let score = "N/A";
      if (details?.pretendentes?.[0]?.laudo?.parecer_sistemico?.[0]?.score_fc) {
        score = details.pretendentes[0].laudo.parecer_sistemico[0].score_fc;
      } else if (details?.pretendentes?.[0]?.produtos?.[0]?.score) {
        score = details.pretendentes[0].produtos[0].score;
      } else if (details?.data?.pretendentes?.[0]?.produtos?.[0]?.score) {
        score = details.data.pretendentes[0].produtos[0].score;
      } else if (details?.data?.laudo?.parecer_sistemico?.[0]?.score_fc) {
        score = details.data.laudo.parecer_sistemico[0].score_fc;
      } else if (details?.parecer_sistemico?.[0]?.score_fc) {
        score = details.parecer_sistemico[0].score_fc;
      }

      const statusFC = details?.solicitacao?.status || details?.status || details?.data?.status || "EM ANÁLISE";

      let riscoStr = "EM ANÁLISE";
      let corStr = "yellow";

      if (statusFC === 'FINALIZADO' || statusFC === 'CONCLUIDA' || statusFC === 'CONCLUIDO') {
        const numScore = parseInt(score, 10);
        if (!isNaN(numScore)) {
          if (numScore >= 600) { riscoStr = "BAIXO RISCO"; corStr = "green"; }
          else if (numScore >= 400) { riscoStr = "MÉDIO RISCO"; corStr = "yellow"; }
          else if (numScore > 0) { riscoStr = "ALTO RISCO"; corStr = "red"; }
          else { riscoStr = "CONCLUÍDO"; corStr = "green"; }
        } else {
          riscoStr = "SEM SCORE/MERCADO";
          corStr = "yellow";
        }
      }

      const updatedResult = {
        score: score,
        risco: riscoStr,
        cor: corStr,
        fcId: fcId,
        rawData: details
      };

      setFcResults(prev => ({ ...prev, [parteId]: { loading: false, data: updatedResult } }));

      const parteRef = doc(db, `requests/${docRefId}/partes`, parteId);
      await updateDoc(parteRef, { fcLastResult: updatedResult });

    } catch (error) {
      console.error("Erro ao atualizar FC:", error);
      alert("Erro ao consultar o Bureau. Verifique a aba Console.");
      setFcResults(prev => ({ ...prev, [parteId]: { ...prev[parteId], loading: false } }));
    }
  };

  const handleEnviarAnaliseFC = async () => {
    if (partes.length === 0) return;
    const confirmar = window.confirm(`Deseja disparar ${partes.length} consultas reais ao Bureau FC Análises?`);
    if (!confirmar) return;

    for (const parte of partes) {
      await executarAnaliseFCIndividual(parte);
    }
  };

  const handleVerLaudoPDF = async (parteId) => {
    const fcId = fcResults[parteId]?.data?.fcId;
    if (!fcId) return;

    try {
      setIsUpdating(true);
      const pdfUrl = await getLinkLaudoPDF(fcId);
      if (pdfUrl && typeof pdfUrl === 'string' && pdfUrl.startsWith('http')) {
        window.open(pdfUrl, '_blank');
      } else {
        alert("O PDF ainda não está disponível ou a URL retornada é inválida neste ambiente de homologação.");
      }
    } catch (error) {
      console.warn("Ambiente Sandbox detectado (PDF 404).");
      alert("Laudo em PDF indisponível no ambiente de testes (Sandbox).");
    } finally {
      setIsUpdating(false);
    }
  };

  // 🟢 FASE 1: NOVA FUNÇÃO DE ATESTE DA REAL (SUPERADMIN)
  const handleAtestarEConcluir = async () => {
    const confirmar = window.confirm("Deseja atestar as assinaturas? O sistema preparará a integração financeira e emitirá a cobrança.");
    if (!confirmar) return;

    setIsUpdating(true);
    try {
      const docRef = doc(db, "requests", docRefId);

      await updateDoc(docRef, { contractStatus: 'closed', paymentStatus: 'pending' });
      await executarMudancaStatus('Aguardando Pagamento', 'Assinaturas atestadas pela Real Benefícios. Cobrança de ativação gerada.');

    } catch (error) {
      console.error("Erro ao atestar contrato:", error);
      alert("Houve um erro ao processar o ateste.");
    } finally {
      setIsUpdating(false);
    }
  };

  // ============================================================================
  // COMPILAÇÃO DOS DOCUMENTOS ASSINADOS (CLICKSIGN)
  // ============================================================================
  const getSignedDocuments = () => {
    if (!solicitacao) return [];
    
    const docs = [];
    
    if (solicitacao.clickSignTermoGarantiaUrl || solicitacao.urlContratoAssinado) {
      docs.push({
        nome: "Termo de Garantia",
        url: solicitacao.clickSignTermoGarantiaUrl || solicitacao.urlContratoAssinado
      });
    }
    
    if (solicitacao.clickSignCartaFiancaUrl || solicitacao.clicksignUrl) {
      docs.push({
        nome: "Carta Fiança",
        url: solicitacao.clickSignCartaFiancaUrl || solicitacao.clicksignUrl
      });
    }

    if (solicitacao.clickSignAnexosUrls && Array.isArray(solicitacao.clickSignAnexosUrls)) {
      solicitacao.clickSignAnexosUrls.forEach(anexo => {
        if (anexo.url) {
          docs.push({
            nome: anexo.nome || "Anexo Assinado",
            url: anexo.url
          });
        }
      });
    }

    // 4. Fluxo Híbrido: Documentos Físicos da Imobiliária (Upload Manual)
    if (solicitacao.documentosAssinados && Array.isArray(solicitacao.documentosAssinados)) {
      solicitacao.documentosAssinados.forEach(docFisico => {
        if (docFisico.url) {
          docs.push({
            nome: docFisico.nome || "Contrato Físico Anexado",
            url: docFisico.url,
            isFisico: true // <-- Injetando flag de origem
          });
        }
      });
    }

    return docs;
  };


  // ============================================================================
  // 6. FUNÇÕES DE MODAIS E PARECER
  // ============================================================================
  const abrirModalParecer = (tipo) => {
    setParecerType(tipo);
    setParecerText("");
    setIsModalOpen(true);
  };

  const abrirModalDocs = (parte) => {
    setParteSelecionada(parte);
    setIsDocModalOpen(true);
  };

  const abrirModalDados = (parte) => {
    setParteDadosSelecionada(parte);
    setIsDataModalOpen(true);
  };

  const handleAbrirTodosDocs = () => {
    if (!parteSelecionada?.documentos || parteSelecionada.documentos.length === 0) return;
    alert("Dica: Os navegadores bloqueiam a abertura de múltiplas abas ao mesmo tempo. Se os arquivos não abrirem, localize o ícone de 'Pop-ups Bloqueados' na barra de endereços lá em cima e clique em 'Sempre permitir'.");
    parteSelecionada.documentos.forEach((docItem) => {
      if (docItem.url) {
        window.open(docItem.url, '_blank');
      }
    });
  };

  const confirmarParecer = async () => {
    if (parecerText.trim() === "") {
      alert("Por favor, digite um motivo/parecer ou escolha um modelo.");
      return;
    }

    if (parecerType === 'Reprovar Contrato') {
      setIsUpdating(true);
      try {
        const docRef = doc(db, "requests", docRefId);
        await updateDoc(docRef, {
          contractStatus: 'draft',
          updatedAt: serverTimestamp(),
          historico: arrayUnion({
            data: new Date().toISOString(),
            status: "Assinaturas Reprovadas",
            mensagem: parecerText,
            autor: userInfo?.name || "SuperAdmin"
          })
        });
        setContractStatus('draft');
      } catch (error) {
        console.error("Erro ao reprovar contrato:", error);
        alert("Houve um erro ao tentar reprovar as assinaturas.");
      } finally {
        setIsUpdating(false);
        setIsModalOpen(false);
      }
    } else {
      executarMudancaStatus(parecerType, parecerText);
      setIsModalOpen(false);
    }
  };

  const handleSelectModelo = (e) => {
    const templateId = e.target.value;
    if (!templateId) return;
    const templateEscolhido = modelosParecer.find(m => m.id === templateId);
    if (templateEscolhido) {
      setParecerText(templateEscolhido.descricao);
    }
  };

  // ============================================================================
  // RENDERIZAÇÃO
  // ============================================================================
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] animate-fade-in">
        <Loader2 className="w-12 h-12 text-brand-primary animate-spin mb-4" />
        <p className="text-slate-500 font-medium">Carregando ficha de análise...</p>
      </div>
    );
  }

  if (!solicitacao) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center animate-fade-in">
        <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mb-6 shadow-sm border border-slate-100">
          <FileText className="w-10 h-10 text-slate-300" />
        </div>
        <h2 className="text-xl font-bold text-slate-800 mb-2">Solicitação não encontrada</h2>
        <p className="text-slate-500 mb-6 font-medium">O protocolo {protocoloReal} não existe ou foi excluído.</p>
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 px-6 py-3 bg-brand-primary text-white rounded-full font-semibold hover:bg-teal-600 transition-colors shadow-md">
          <ArrowLeft className="w-4 h-4"/> Voltar para lista
        </button>
      </div>
    );
  }

  const hasAnyProcessedFc = partes.some(p => p.fcStatus === 'FINALIZADO' || p.fcStatus === 'CONCLUIDA' || p.fcStatus === 'CONCLUIDO' || fcResults[p.id]?.data?.fcId);
  const showClickSignPdf = solicitacao.status === 'Garantia Ativa' || solicitacao.contractStatus === 'awaiting_real_validation' || solicitacao.contractStatus === 'closed';
  const showCofreJuridico = hasAnyProcessedFc || showClickSignPdf;

  // Lógica segura aplicada nos valores
  const vAluguel = parseCurrencyToFloat(solicitacao.imovel?.aluguel);
  const vCondominio = parseCurrencyToFloat(solicitacao.imovel?.condominio);
  const vIptu = parseCurrencyToFloat(solicitacao.imovel?.iptu);
  const vAgua = parseCurrencyToFloat(solicitacao.imovel?.agua);
  const vLuz = parseCurrencyToFloat(solicitacao.imovel?.luz);
  const vSeguroIncendio = parseCurrencyToFloat(solicitacao.imovel?.seguroIncendio);

  const totalMensalEncargos = vAluguel + vCondominio + vIptu + vAgua + vLuz + vSeguroIncendio;
  const totalMensalStr = totalMensalEncargos.toLocaleString('pt-BR', { minimumFractionDigits: 2 });

  const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

  const inquilinosQueCompoem = partes.filter(p => p.perfilFinanceiro?.compoeRenda);
  const totalRendaGlobal = inquilinosQueCompoem.reduce((acc, p) => {
    const rendasParte = p.perfilFinanceiro?.rendas || [];
    const somaParte = rendasParte.reduce((sub, r) => {
      const val = parseCurrencyToFloat(r.valor);
      return sub + val;
    }, 0);
    return acc + somaParte;
  }, 0);

  const comprometimentoRenda = totalRendaGlobal > 0 ? ((totalMensalEncargos / totalRendaGlobal) * 100).toFixed(1) : "0,0";

  const partesComScore = partes.map(p => {
    const fcData = fcResults[p.id]?.data;
    return {
      risco: fcData?.risco || "EM ANÁLISE",
      cor: fcData?.cor || "yellow"
    };
  });

  const riscoGeralInquilinos = partesComScore.some(p => p.risco === "ALTO RISCO") ? "ALTO RISCO" :
                               partesComScore.some(p => p.risco === "MÉDIO RISCO") ? "MÉDIO RISCO" :
                               partesComScore.every(p => p.risco === "BAIXO RISCO") ? "BAIXO RISCO" : "EM ANÁLISE";

  const corRiscoGeral = riscoGeralInquilinos === "ALTO RISCO" ? "red" :
                        riscoGeralInquilinos === "MÉDIO RISCO" ? "yellow" :
                        riscoGeralInquilinos === "BAIXO RISCO" ? "green" : "yellow";

  // MÁQUINA DE ESTADOS VISUAIS DA REAL
  const isDraft = contractStatus === 'draft' || contractStatus === 'running' || contractStatus === 'awaiting_real_validation' || contractStatus === 'closed' || solicitacao.status === 'Garantia Ativa';
  const isRunning = contractStatus === 'running' || contractStatus === 'awaiting_real_validation' || contractStatus === 'closed' || solicitacao.status === 'Garantia Ativa';
  const isAwaitingValidation = contractStatus === 'awaiting_real_validation' || contractStatus === 'closed' || solicitacao.status === 'Garantia Ativa';
  const isClosed = contractStatus === 'closed' || solicitacao.status === 'Garantia Ativa';

  return (
    <>
      <div className="relative z-0 p-4 sm:p-5 xl:p-8 space-y-5 xl:space-y-6 animate-fade-in bg-slate-50 min-h-full font-sans">

        {/* BREADCRUMB */}
        <div className="flex items-center text-sm text-slate-500 font-medium mb-2 xl:mb-4">
          <Link to="/super-admin/solicitacoes" className="hover:text-brand-primary transition-colors">Solicitações</Link>
          <ChevronRight className="w-4 h-4 mx-1" />
          <span className="font-semibold text-slate-800">Análise {solicitacao.protocolo}</span>
        </div>

        {/* HEADER DE AÇÕES E STATUS */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 xl:p-6 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 xl:gap-6 relative overflow-hidden">
          {isUpdating && (
            <div className="absolute inset-0 bg-white/60 backdrop-blur-sm z-10 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
            </div>
          )}

          <div className="flex-1 min-w-0 pr-4">
            <div className="flex flex-wrap items-center gap-2 xl:gap-3 mb-2">
              <h1 className="text-lg xl:text-xl font-bold text-slate-800 truncate flex items-center gap-2">
                Solicitação {solicitacao.protocolo}

                {isLocalhost && (
                  <button
                    onClick={handleCadastrarWebhook}
                    title="Registra a URL do Ngrok na API da FC"
                    className="text-[10px] bg-purple-100 text-purple-700 border border-purple-200 px-2 py-1 rounded shadow-sm hover:bg-purple-200 transition-colors flex items-center gap-1 ml-2"
                  >
                    <Zap className="w-3 h-3"/> Cadastrar Webhook Ngrok
                  </button>
                )}
              </h1>
              <span className={`px-2 xl:px-3 py-0.5 xl:py-1 rounded-full text-xs font-semibold border shadow-sm whitespace-nowrap
                ${solicitacao.status === 'Garantia Ativa' ? 'bg-green-600 text-white border-green-700' :
                  solicitacao.status === 'Aprovada' ? 'bg-green-100 text-green-700 border-green-200' :
                  solicitacao.status === 'Rejeitada' ? 'bg-red-100 text-red-700 border-red-200' :
                  solicitacao.status === 'Aguardando Pagamento' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' :
                  solicitacao.status === 'Pendente' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' :
                  'bg-blue-100 text-blue-700 border-blue-200'}`}>
                {solicitacao.status}
              </span>

              {/* 🟢 PILL COM O VALOR DA COMISSÃO */}
              {(solicitacao.taxaAtivacao || condicoesAprovacao.valorTaxa) && (
                <span className="px-2 xl:px-3 py-0.5 xl:py-1 rounded-full text-xs font-bold border shadow-sm whitespace-nowrap bg-emerald-100 text-emerald-700 border-emerald-200">
                  Comissão Acordada: R$ {solicitacao.taxaAtivacao || condicoesAprovacao.valorTaxa}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 xl:gap-y-2 text-xs xl:text-sm text-slate-500 font-medium">
              <span className="flex items-center gap-1 font-semibold text-slate-700 truncate max-w-[200px] xl:max-w-none" title={solicitacao.companyName}><Building2 className="w-3.5 h-3.5 xl:w-4 xl:h-4 shrink-0 text-slate-400"/> <span className="truncate">{solicitacao.companyName}</span></span>
              <span className="flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 xl:w-4 xl:h-4 text-slate-400"/> {solicitacao.servico || "Fiança Locatícia"}</span>
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 xl:w-4 xl:h-4 text-slate-400"/> {solicitacao.dataSolicitacaoTexto}</span>
            </div>
          </div>

          {/* PAINEL DE DECISÃO */}
          <div className="flex flex-wrap gap-2 xl:gap-3 w-full lg:w-auto bg-slate-50 p-2 rounded-xl border border-slate-200 shrink-0">
            {solicitacao.status === 'Garantia Ativa' ? (
              <>
                <span className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-green-800 bg-green-100 border border-green-300 rounded-lg cursor-default shadow-sm w-full sm:w-auto">
                  <CheckCircle className="w-4 h-4"/> Processo Concluído
                </span>
              </>
            ) : solicitacao.status === 'Aprovada' || solicitacao.status === 'Aguardando Pagamento' ? (
              <>
                <span className="flex items-center justify-center gap-2 px-3 xl:px-4 py-2 text-xs xl:text-sm font-semibold text-green-700 bg-green-100 border border-green-200 rounded-lg cursor-not-allowed w-full sm:w-auto">
                  <CheckCircle className="w-4 h-4"/> Ficha Aprovada
                </span>

                {!contractStatus || contractStatus === 'loading' ? (
                  <button
                    disabled={isUpdating || contractStatus === 'loading'}
                    onClick={handleLiberarParaAssinaturas}
                    className="flex items-center justify-center gap-2 px-3 xl:px-4 py-2 text-xs xl:text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-md disabled:opacity-50 w-full sm:w-auto"
                  >
                    <Send className="w-4 h-4"/> Liberar p/ Assinaturas
                  </button>
                ) : (
                  <span className="flex items-center justify-center gap-2 px-3 xl:px-4 py-2 text-xs xl:text-sm font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg cursor-default w-full sm:w-auto">
                    <FileSignature className="w-4 h-4"/> Aguardando Processo
                  </span>
                )}

                <button
                  disabled={isUpdating}
                  onClick={() => handleRevogar('Aprovada')}
                  className="flex items-center justify-center gap-2 px-3 xl:px-4 py-2 text-xs xl:text-sm font-semibold text-slate-600 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg transition-colors w-full sm:w-auto shadow-sm"
                >
                  <RotateCcw className="w-4 h-4"/> Revogar Aprovação
                </button>
              </>
            ) : (
              <>
                <button
                  disabled={isUpdating || partes.length === 0}
                  onClick={handleEnviarAnaliseFC}
                  className="flex flex-1 sm:flex-none items-center justify-center gap-1.5 xl:gap-2 px-3 xl:px-4 py-2 text-xs xl:text-sm font-semibold text-blue-700 bg-blue-100 hover:bg-blue-200 border border-blue-200 rounded-lg transition-colors disabled:opacity-50 shadow-sm"
                >
                  <Activity className="w-3.5 h-3.5 xl:w-4 xl:h-4 shrink-0"/> FC Análises
                </button>

                <button
                  disabled={isUpdating}
                  onClick={() => abrirModalParecer('Pendente')}
                  className="flex flex-1 sm:flex-none items-center justify-center gap-1.5 xl:gap-2 px-3 xl:px-4 py-2 text-xs xl:text-sm font-semibold text-yellow-700 bg-yellow-100 hover:bg-yellow-200 border border-yellow-200 rounded-lg transition-colors disabled:opacity-50 shadow-sm"
                >
                  <AlertCircle className="w-3.5 h-3.5 xl:w-4 xl:h-4 shrink-0"/> Pendência
                </button>
                <button
                  disabled={isUpdating}
                  onClick={() => abrirModalParecer('Rejeitada')}
                  className="flex flex-1 sm:flex-none items-center justify-center gap-1.5 xl:gap-2 px-3 xl:px-4 py-2 text-xs xl:text-sm font-semibold text-red-700 bg-red-100 hover:bg-red-200 border border-red-200 rounded-lg transition-colors disabled:opacity-50 shadow-sm"
                >
                  <XCircle className="w-3.5 h-3.5 xl:w-4 xl:h-4 shrink-0"/> Reprovar
                </button>

                <div className="relative group flex flex-1 sm:flex-none w-full sm:w-auto">
                  <button
                    disabled={isUpdating || !isCondicoesSaved}
                    onClick={handleAprovarDireto}
                    className={`flex flex-1 sm:flex-none items-center justify-center gap-1.5 xl:gap-2 px-3 xl:px-4 py-2 text-xs xl:text-sm font-semibold rounded-lg transition-all shadow-sm w-full sm:w-auto
                      ${isCondicoesSaved
                        ? 'text-white bg-brand-primary hover:bg-teal-600 shadow-brand-primary/30'
                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
                  >
                    <CheckCircle className="w-3.5 h-3.5 xl:w-4 xl:h-4 shrink-0"/> Aprovar
                  </button>

                  {!isCondicoesSaved && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-48 bg-slate-800 text-white text-[10px] text-center p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                      Salve as "Condições de Aprovação" no menu lateral para liberar a aprovação da ficha.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* GRID PRINCIPAL DA TELA */}
        <div className="grid grid-cols-1 lg:grid-cols-12 xl:grid-cols-3 gap-5 xl:gap-6">

          {/* COLUNA ESQUERDA (Condições, Cofre e Imóvel) */}
          <div className="lg:col-span-5 xl:col-span-1 space-y-5 xl:space-y-6">

            {/* CARD DE CONDIÇÕES DE APROVAÇÃO */}
              {(solicitacao.status === 'Em Analise' || solicitacao.status === 'Pendente' || solicitacao.status === 'Legado' || solicitacao.status === 'LEGADO' || isCondicoesSaved) && (
                <div className={`bg-white rounded-2xl border ${isCondicoesSaved ? 'border-brand-primary ring-1 ring-brand-primary/20' : 'border-slate-200'} shadow-sm p-5 xl:p-6 transition-all`}>
                <div className="flex justify-between items-center mb-4 border-b border-slate-100 pb-3">
                  <h2 className="text-base font-bold text-slate-800 flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 xl:w-5 xl:h-5 text-brand-primary"/> Condições de Aprovação
                  </h2>
                  {isCondicoesSaved && <div title="Condições Salvas"><CheckCircle className="w-4 h-4 text-brand-primary" /></div>}
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="flex items-center gap-1 text-xs font-medium text-slate-500 mb-1.5">
                      <Percent className="w-3 h-3" /> Taxa Aprovada
                    </label>
                    <select
                      disabled={solicitacao.status === 'Aprovada' || solicitacao.status === 'Aguardando Pagamento' || solicitacao.status === 'Garantia Ativa'}
                      value={condicoesAprovacao.percentual}
                      onChange={(e) => {
                        const pct = e.target.value;
                        let valCalculado = condicoesAprovacao.valorTaxa;

                        if (pct && totalMensalEncargos > 0) {
                          const percNum = parseFloat(pct.replace('%', '').replace(',', '.'));
                          if (!isNaN(percNum)) {
                            valCalculado = (totalMensalEncargos * (percNum / 100)).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                          }
                        }

                        setCondicoesAprovacao(prev => ({...prev, percentual: pct, valorTaxa: valCalculado}));
                        setIsCondicoesSaved(false);
                      }}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      <option value="" disabled>Selecione...</option>
                      <option value="8%">8%</option>
                      <option value="10%">10%</option>
                      <option value="12%">12%</option>
                    </select>
                  </div>

                  <div>
                    <label className="flex items-center gap-1 text-xs font-medium text-slate-500 mb-1.5">
                      <DollarSign className="w-3 h-3" /> Valor da Taxa (Mensal)
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-slate-400 font-semibold text-sm">R$</span>
                      </div>
                      <input
                        disabled={solicitacao.status === 'Aprovada' || solicitacao.status === 'Aguardando Pagamento' || solicitacao.status === 'Garantia Ativa'}
                        type="text"
                        value={condicoesAprovacao.valorTaxa}
                        onChange={(e) => {
                          setCondicoesAprovacao(prev => ({...prev, valorTaxa: maskCurrency(e.target.value)}));
                          setIsCondicoesSaved(false);
                        }}
                        placeholder="0,00"
                        className="w-full pl-10 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary disabled:opacity-70 disabled:cursor-not-allowed"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="flex items-center gap-1 text-xs font-medium text-slate-500 mb-1.5">
                      <CreditCard className="w-3 h-3" /> Forma de Pagamento
                    </label>
                    <select
                      disabled={solicitacao.status === 'Aprovada' || solicitacao.status === 'Aguardando Pagamento' || solicitacao.status === 'Garantia Ativa'}
                      value={condicoesAprovacao.formaPagamento}
                      onChange={(e) => {
                        setCondicoesAprovacao(prev => ({...prev, formaPagamento: e.target.value}));
                        setIsCondicoesSaved(false);
                      }}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      <option value="" disabled>Selecione...</option>
                      <option value="À Vista">À Vista</option>
                      <option value="Boleto Imobiliária">Boleto Imobiliária</option>
                      <option value="Cartão de Crédito">Cartão de Crédito</option>
                    </select>
                  </div>

                  {(solicitacao.status === 'Em Analise' || solicitacao.status === 'Pendente' || solicitacao.status === 'Legado' || solicitacao.status === 'LEGADO') && (
                      <div className="pt-2">
                        <button
                          onClick={handleSaveCondicoes}
                          disabled={isSavingCondicoes}
                          className={`w-full py-2.5 rounded-xl text-xs font-semibold flex items-center justify-center gap-2 transition-all shadow-sm ${isCondicoesSaved ? 'bg-slate-100 text-slate-500 hover:bg-slate-200' : 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100'}`}
                        >
                          {isSavingCondicoes ? <Loader2 className="w-4 h-4 animate-spin" /> : isCondicoesSaved ? <CheckCircle className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                          {isSavingCondicoes ? "Salvando..." : isCondicoesSaved ? "Atualizar Condições" : "Salvar Condições"}
                        </button>
                      </div>
                    )}
                </div>
              </div>
            )}

            {/* 🟢 FLUXO DO CONTRATO - NOVO MOTOR (BLOCO 3) */}
            {contractStatus && contractStatus !== 'loading' && (
              <div className="bg-white rounded-2xl border-l-4 border-l-blue-500 border border-slate-200 shadow-sm p-5 xl:p-6 animate-fade-in">
                <h2 className="text-base font-bold text-slate-800 flex items-center gap-2 mb-4 xl:mb-6 border-b border-slate-100 pb-3">
                  <FileSignature className="w-4 h-4 xl:w-5 xl:h-5 text-blue-500"/> Gestão de Assinaturas & Integrações
                </h2>

                <div className="space-y-0 relative">
                  <div className="absolute top-4 bottom-8 left-4 w-0.5 bg-slate-200"></div>

                  <div className="flex gap-3 xl:gap-4 relative pb-5 xl:pb-6">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ring-4 ring-white ${isDraft ? 'bg-blue-500 text-white shadow-md' : 'bg-slate-100 text-slate-400'}`}>
                      <FileText className="w-3.5 h-3.5 xl:w-4 xl:h-4"/>
                    </div>
                    <div className="pt-1.5 w-full">
                      <div className="flex flex-col gap-2">
                        <div>
                          <p className={`font-semibold text-sm ${isDraft ? 'text-slate-800' : 'text-slate-400'}`}>Envelope Base Configurado (Draft)</p>
                          <p className="text-xs text-slate-500 font-medium mt-0.5">Modelos do ClickSign já mapeados no Motor. Ficha liberada para a Imobiliária iniciar o processo.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 xl:gap-4 relative pb-5 xl:pb-6">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ring-4 ring-white ${isRunning ? 'bg-yellow-500 text-white shadow-md' : isDraft ? 'bg-white border-2 border-yellow-500 text-yellow-600' : 'bg-slate-100 text-slate-400'}`}>
                      <Send className="w-3.5 h-3.5 xl:w-4 xl:h-4"/>
                    </div>
                    <div className="pt-1.5">
                      <p className={`font-semibold text-sm ${isRunning ? 'text-slate-800' : 'text-slate-400'}`}>Assinaturas em Andamento</p>
                      <p className="text-xs text-slate-500 font-medium mt-0.5">
                        {isRunning ? 'Imobiliária acionou o Motor e o envelope digital está em execution via ClickSign.' : 'Aguardando Imobiliária subir o contrato de locação e iniciar as assinaturas.'}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3 xl:gap-4 relative pb-5 xl:pb-6">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ring-4 ring-white ${isAwaitingValidation ? 'bg-blue-500 text-white shadow-md' : 'bg-slate-100 text-slate-400'}`}>
                      <Search className="w-3.5 h-3.5 xl:w-4 xl:h-4"/>
                    </div>
                    <div className="pt-1.5 w-full">
                      <p className={`font-semibold text-sm ${isAwaitingValidation ? 'text-slate-800' : 'text-slate-400'}`}>Auditoria Real Benefícios</p>

                      {isAwaitingValidation && !isClosed ? (
                        <div className="mt-3 bg-blue-50 border border-blue-200 p-4 rounded-xl flex flex-col gap-3">
                          <div>
                            <p className="text-sm font-semibold text-blue-800 mb-1">Homologação Necessária</p>
                            <p className="text-xs text-blue-700 font-medium">A imobiliária concluiu a coleta de assinaturas. Revise os documentos e ateste para faturar.</p>
                          </div>

                          {/* FASE 1 E ROTA DE FUGA: VISUALIZAR, HOMOLOGAR OU REPROVAR */}
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 w-full mt-1">
                            <button
                              onClick={() => {
                                const docs = getSignedDocuments();
                                if (docs.length > 0) {
                                  setIsSignedDocsModalOpen(true);
                                } else {
                                  alert("Os arquivos assinados ainda não estão disponíveis ou não foram indexados.");
                                }
                              }}
                              disabled={isUpdating}
                              className="w-full flex items-center justify-center gap-1.5 px-2 py-2 bg-white text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors font-bold text-xs shadow-sm disabled:opacity-50"
                            >
                              <Eye className="w-3.5 h-3.5 shrink-0"/> <span className="truncate">Ver Contrato</span>
                            </button>

                            <button
                              onClick={() => abrirModalParecer('Reprovar Contrato')}
                              disabled={isUpdating}
                              className="w-full flex items-center justify-center gap-1.5 px-2 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-bold text-xs shadow-sm disabled:opacity-50"
                            >
                              <XCircle className="w-3.5 h-3.5 shrink-0"/> <span className="truncate">Reprovar</span>
                            </button>

                            <button
                              onClick={handleAtestarEConcluir}
                              disabled={isUpdating}
                              className="w-full flex items-center justify-center gap-1.5 px-2 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-bold text-xs shadow-sm disabled:opacity-50"
                            >
                              <CheckCircle className="w-3.5 h-3.5 shrink-0"/> <span className="truncate">Homologar</span>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-slate-500 mt-0.5">
                          {isClosed ? 'Documentação homologada pela equipe.' : 'Aguardando conclusão das assinaturas.'}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* PASSO 4: FATURAMENTO / ASAAS */}
                  <div className="flex gap-3 xl:gap-4 relative">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ring-4 ring-white ${isClosed ? 'bg-green-500 text-white shadow-md' : 'bg-slate-100 text-slate-400'}`}>
                      <CheckCircle className="w-3.5 h-3.5 xl:w-4 xl:h-4"/>
                    </div>
                    <div className="pt-1.5 w-full">
                      <p className={`font-semibold text-sm ${isClosed ? 'text-green-600' : 'text-slate-400'}`}>Ativação & Faturamento</p>

                      {!isClosed ? (
                        <p className="text-xs text-slate-500 mt-0.5">Aguardando ateste para ativar.</p>
                      ) : (
                        <div className="mt-3 bg-green-50 border border-green-200 p-4 rounded-xl flex flex-col gap-4">
                          {solicitacao.paymentStatus === 'paid' ? (
                            <p className="text-sm font-bold flex items-center gap-2 text-green-700">
                              <CheckCircle className="w-4 h-4"/> Pagamento Confirmado
                            </p>
                          ) : (
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 w-full">
                              <div>
                                <p className="text-sm font-bold flex items-center gap-2 text-yellow-600">
                                  <Clock className="w-4 h-4"/> Boleto Emitido - Aguardando
                                </p>
                                <p className="text-[10px] text-yellow-700 mt-1 font-medium">Aguardando confirmação do Asaas.</p>
                              </div>
                            </div>
                          )}

                          {/* DESCRITIVO MATEMÁTICO (FASE 3) */}
                          <div className="bg-white border border-green-100 p-3 rounded-lg shadow-sm">
                            <p className="text-xs font-bold text-slate-700 mb-2 border-b border-slate-100 pb-1">Descritivo da Cobrança (Forma: {condicoesAprovacao.formaPagamento || "Não definida"})</p>
                            {condicoesAprovacao.formaPagamento === 'Boleto Imobiliária' ? (
                              <div className="space-y-1.5">
                                  <div className="flex justify-between text-xs text-slate-600">
                                      <span>Taxa de Ativação (Comissão)</span>
                                      <span>R$ {solicitacao.taxaAtivacao || "0,00"}</span>
                                  </div>
                                  <div className="flex justify-between text-xs text-slate-400 line-through">
                                      <span>1ª Mensalidade da Garantia</span>
                                      <span>R$ {condicoesAprovacao.valorTaxa || "0,00"}</span>
                                  </div>
                                  <div className="flex justify-between text-xs font-bold text-slate-800 mt-2 pt-1.5 border-t border-slate-100">
                                      <span>Total do Boleto (Inquilino)</span>
                                      <span className="text-green-700">R$ {solicitacao.taxaAtivacao || "0,00"}</span>
                                  </div>
                                  <p className="text-[10px] text-blue-600 mt-1.5 italic font-medium">* O valor da mensalidade será embutido no faturamento B2B mensal da imobiliária.</p>
                              </div>
                            ) : (
                              <div className="space-y-1.5">
                                  <div className="flex justify-between text-xs text-slate-600">
                                      <span>Taxa de Ativação (Comissão)</span>
                                      <span>R$ {solicitacao.taxaAtivacao || "0,00"}</span>
                                  </div>
                                  <div className="flex justify-between text-xs text-slate-600">
                                      <span>1ª Mensalidade da Garantia</span>
                                      <span>R$ {condicoesAprovacao.valorTaxa || "0,00"}</span>
                                  </div>
                                  <div className="flex justify-between text-xs font-bold text-slate-800 mt-2 pt-1.5 border-t border-slate-100">
                                      <span>Total do Boleto (Inquilino)</span>
                                      <span className="text-green-700">R$ {calcularSoma(solicitacao.taxaAtivacao, condicoesAprovacao.valorTaxa)}</span>
                                  </div>
                                  <p className="text-[10px] text-slate-500 mt-1.5 italic font-medium">* O Inquilino pagará a soma da Ativação + 1ª Mensalidade no mesmo boleto.</p>
                              </div>
                            )}
                          </div>

                        </div>
                      )}
                    </div>
                  </div>

                </div>
              </div>
            )}

            {/* COFRE JURÍDICO */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 xl:p-6">
              <h2 className="text-base font-bold text-slate-800 flex items-center gap-2 mb-4 border-b border-slate-100 pb-3">
                <Lock className="w-4 h-4 xl:w-5 xl:h-5 text-slate-500"/> Cofre Jurídico (Restrito)
              </h2>
              <div className="space-y-3">
                 <p className="text-xs text-slate-500 mb-3 xl:mb-4 leading-relaxed font-medium">
                   Documentos oficiais gerados automaticamente pela plataforma. Visíveis apenas para a equipe da Real Benefícios.
                 </p>

                 {showCofreJuridico ? (
                   <>
                     {/* Mapeamento dinâmico dos Dossiês da FC Análises */}
                     {partes.map((parte) => {
                        const parteFcData = fcResults[parte.id];
                        // Mostra apenas se o laudo já estiver concluído no sistema
                        if (!parteFcData || !parteFcData.data || parteFcData.data.risco === 'EM ANÁLISE' || parteFcData.data.risco === 'PROCESSANDO') return null;

                        return (
                          <div key={`cofre-fc-${parte.id}`} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 xl:p-4 bg-slate-50 rounded-xl border border-slate-100 gap-3">
                             <div className="flex items-center gap-2 xl:gap-3">
                                <div className="w-7 h-7 xl:w-8 xl:h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                                  <Activity className="w-3.5 h-3.5 xl:w-4 xl:h-4" />
                                </div>
                                <div>
                                  <p className="text-xs xl:text-sm font-semibold text-slate-700">Dossiê: {parte.dadosPessoais?.nome?.split(' ')[0] || "Inquilino"}</p>
                                  <p className="text-xs text-slate-500 mt-0.5">FC Análises</p>
                                </div>
                             </div>
                             <button onClick={() => handleVerLaudoPDF(parte.id)} className="text-brand-primary text-xs font-semibold hover:underline bg-brand-primary/10 px-2 xl:px-3 py-1.5 rounded-lg w-full sm:w-auto text-center">Abrir PDF</button>
                          </div>
                        );
                     })}

                     {showClickSignPdf && (
                         <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 xl:p-4 bg-slate-50 rounded-xl border border-slate-100 gap-3">
                            <div className="flex items-center gap-2 xl:gap-3">
                               <div className="w-7 h-7 xl:w-8 xl:h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center shrink-0">
                                 <FileSignature className="w-3.5 h-3.5 xl:w-4 xl:h-4" />
                               </div>
                               <div>
                                 <p className="text-xs xl:text-sm font-semibold text-slate-700">Documentos Assinados</p>
                                 <p className="text-xs text-slate-500 mt-0.5">ClickSign</p>
                               </div>
                            </div>
                            <button
                              onClick={() => {
                                const docs = getSignedDocuments();
                                if (docs.length > 0) {
                                  setIsSignedDocsModalOpen(true);
                                } else {
                                  alert("Os arquivos assinados ainda não estão disponíveis ou não foram indexados.");
                                }
                              }}
                              className="text-brand-primary text-xs font-semibold hover:underline bg-brand-primary/10 px-2 xl:px-3 py-1.5 rounded-lg w-full sm:w-auto text-center"
                            >
                              Abrir PDFs
                            </button>
                         </div>
                     )}
                   </>
                 ) : (
                     <div className="text-center py-4 xl:py-6 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50">
                       <Lock className="w-5 h-5 xl:w-6 xl:h-6 text-slate-300 mx-auto mb-2" />
                       <p className="text-xs text-slate-400 font-medium">Nenhum documento restrito gerado.</p>
                     </div>
                 )}
              </div>
            </div>

            {/* DADOS DO IMÓVEL */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 xl:p-6">
              <h2 className="text-base font-bold text-slate-800 flex items-center gap-2 mb-4 border-b border-slate-100 pb-3">
                <MapPin className="w-4 h-4 xl:w-5 xl:h-5 text-slate-500"/> Dados do Imóvel
              </h2>
              <div className="space-y-3 xl:space-y-4">
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1">Endereço</p>
                  <p className="text-sm font-semibold text-slate-800">{solicitacao.imovel?.endereco}, {solicitacao.imovel?.numero}</p>
                  <p className="text-sm font-medium text-slate-500 mt-0.5">{solicitacao.imovel?.complemento ? `${solicitacao.imovel.complemento} - ` : ''} {solicitacao.imovel?.bairro}</p>
                  <p className="text-sm font-medium text-slate-500 mt-0.5">{solicitacao.imovel?.cidade}/{solicitacao.imovel?.estado} - CEP: {solicitacao.imovel?.cep}</p>
                </div>
                <div className="grid grid-cols-2 gap-3 xl:gap-4 pt-3 border-t border-slate-100">
                  <div>
                    <p className="text-xs font-medium text-slate-500 mb-1">Código</p>
                    <p className="text-sm font-semibold text-slate-800">{solicitacao.imovel?.codigo || "-"}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 mb-1">Tipo</p>
                    <p className="text-sm font-semibold text-slate-800 capitalize">{solicitacao.imovel?.tipo || "-"}</p>
                  </div>
                </div>
                <div className="pt-3 border-t border-slate-100">
                  <p className="text-xs font-medium text-slate-500 mb-1">Proprietário</p>
                  <p className="text-sm font-semibold text-slate-800">{solicitacao.imovel?.nomeProprietario || "-"}</p>
                  <p className="text-xs font-medium text-slate-500 mt-0.5">CPF/CNPJ: {solicitacao.imovel?.cpfCnpjProprietario || "-"}</p>
                </div>
                <div className="bg-slate-50 p-3 xl:p-4 rounded-xl space-y-2 mt-3 xl:mt-4 border border-slate-100">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-500 font-medium text-xs">Aluguel:</span>
                    <span className="font-semibold text-slate-800">R$ {solicitacao.imovel?.aluguel || "0,00"}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-500 font-medium text-xs">Condomínio:</span>
                    <span className="font-semibold text-slate-800">R$ {solicitacao.imovel?.condominio || "0,00"}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-500 font-medium text-xs">IPTU:</span>
                    <span className="font-semibold text-slate-800">R$ {solicitacao.imovel?.iptu || "0,00"}</span>
                  </div>
                  {solicitacao.imovel?.agua && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-500 font-medium text-xs">Água:</span>
                      <span className="font-semibold text-slate-800">R$ {solicitacao.imovel.agua}</span>
                    </div>
                  )}
                  {solicitacao.imovel?.luz && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-500 font-medium text-xs">Luz:</span>
                      <span className="font-semibold text-slate-800">R$ {solicitacao.imovel.luz}</span>
                    </div>
                  )}
                  {solicitacao.imovel?.seguroIncendio && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-500 font-medium text-xs">Seguro Incêndio:</span>
                      <span className="font-semibold text-slate-800">R$ {solicitacao.imovel.seguroIncendio}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-2 mt-2 border-t border-slate-200">
                    <span className="text-brand-primary font-semibold text-sm">Valor Locatício:</span>
                    <span className="font-bold text-brand-primary text-base">R$ {totalMensalStr}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* OBSERVAÇÕES */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 xl:p-6">
              <h2 className="text-base font-bold text-slate-800 flex items-center gap-2 mb-4 border-b border-slate-100 pb-3">
                <MessageSquare className="w-4 h-4 xl:w-5 xl:h-5 text-slate-500"/> Observações (Imobiliária)
              </h2>
              {solicitacao.observacoes ? (
                <div className="p-3 xl:p-4 bg-yellow-50 rounded-xl border border-yellow-100 text-sm text-slate-800 italic leading-relaxed font-medium">
                  "{solicitacao.observacoes}"
                </div>
              ) : (
                <p className="text-sm text-slate-400 font-medium italic">Nenhuma observação informada.</p>
              )}
            </div>

            {/* HISTÓRICO DE ANÁLISES */}
            {solicitacao.historico && solicitacao.historico.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 xl:p-6">
                <h2 className="text-base font-bold text-slate-800 flex items-center gap-2 mb-4 border-b border-slate-100 pb-3">
                  <Clock className="w-4 h-4 xl:w-5 xl:h-5 text-slate-500"/> Histórico de Análises
                </h2>
                <div className="space-y-3 xl:space-y-4 max-h-60 xl:max-h-80 overflow-y-auto custom-scrollbar pr-2">
                  {[...solicitacao.historico].reverse().map((hist, idx) => (
                    <div key={idx} className={`p-3 xl:p-4 rounded-xl border ${hist.status === 'Rejeitada' ? 'bg-red-50 border-red-100' : hist.status === 'Pendente' ? 'bg-yellow-50 border-yellow-100' : 'bg-slate-50 border-slate-100'}`}>
                      <div className="flex justify-between items-start mb-2">
                        <span className={`text-xs font-semibold ${hist.status === 'Rejeitada' ? 'text-red-600' : hist.status === 'Pendente' ? 'text-yellow-600' : 'text-slate-600'}`}>{hist.status}</span>
                        <span className="text-xs text-slate-400 font-medium">{new Date(hist.data).toLocaleDateString('pt-BR')}</span>
                      </div>
                      <p className="text-sm text-slate-700 font-medium leading-snug">{hist.mensagem}</p>
                      <p className="text-xs text-slate-400 font-medium mt-2 text-right">Por: {hist.autor}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* COLUNA DIREITA (Síntese Geral e Partes) */}
          <div className="lg:col-span-7 xl:col-span-2 space-y-5 xl:space-y-6">

            {/* SÍNTESE GERAL */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 xl:p-6 animate-fade-in">
              <h2 className="text-base font-bold text-slate-800 flex items-center gap-2 mb-4 border-b border-slate-100 pb-3">
                <Scale className="w-4 h-4 xl:w-5 xl:h-5 text-brand-primary"/> Resumo dos Inquilinos (Síntese Geral)
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-slate-50 border border-slate-100 p-4 rounded-xl">
                  <p className="text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wider">Renda Mensal do Grupo</p>
                  <p className="text-lg font-bold text-slate-800">R$ {totalRendaGlobal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                  <p className="text-[10px] text-slate-400 mt-2 font-medium">Soma de quem compõe renda</p>
                </div>

                <div className="bg-slate-50 border border-slate-100 p-4 rounded-xl flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-1.5 mb-1">
                       <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Comprometimento</p>
                    </div>
                    <p className={`text-lg font-bold ${parseFloat(comprometimentoRenda) > 30 ? 'text-red-600' : 'text-green-600'}`}>
                      {comprometimentoRenda}%
                    </p>
                    <div className="w-full bg-slate-200 h-1.5 rounded-full mt-2.5 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${parseFloat(comprometimentoRenda) > 30 ? 'bg-red-500' : 'bg-green-500'}`}
                        style={{ width: `${Math.min(parseFloat(comprometimentoRenda), 100)}%` }}
                      />
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-500 font-medium mt-3 leading-relaxed">
                    Parcela da renda global consumida pelos encargos do imóvel. O limite recomendado é <strong>30%</strong>.
                  </p>
                </div>

                <div className="bg-slate-50 border border-slate-100 p-4 rounded-xl flex flex-col justify-between">
                  <div>
                    <p className="text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wider">Saúde da Análise</p>
                    <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded border mt-1 uppercase ${
                      corRiscoGeral === 'green' ? 'bg-green-50 text-green-600 border-green-200' :
                      corRiscoGeral === 'red' ? 'bg-red-50 text-red-600 border-red-200' : 'bg-yellow-50 text-yellow-600 border-yellow-200'
                    }`}>
                      {riscoGeralInquilinos}
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-400 font-medium mt-2">Visão consolidada do contrato</p>
                </div>
              </div>
            </div>

            {/* LISTAGEM INDIVIDUAL DE PARTES */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 xl:p-6">
              <h2 className="text-base font-bold text-slate-800 flex items-center gap-2 mb-5 xl:mb-6 border-b border-slate-100 pb-3">
                <Users className="w-4 h-4 xl:w-5 xl:h-5 text-brand-primary"/> Partes Envolvidas (Análise de Risco)
              </h2>

              <div className="space-y-4">
                {partes.length === 0 ? (
                  <div className="text-center py-10 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50">
                    <p className="text-slate-400 font-medium text-sm">Nenhuma parte cadastrada encontrada.</p>
                  </div>
                ) : (
                  partes.map((parte) => {
                    const parteFcData = fcResults[parte.id];

                    return (
                      <div key={parte.id} className="border border-slate-200 rounded-xl overflow-hidden hover:border-brand-primary/50 transition-colors bg-slate-50">
                        <div className="p-4 xl:p-5">

                          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                            <div className="flex items-center gap-3 xl:gap-4 w-full sm:w-auto overflow-hidden">
                              <div className="w-10 h-10 xl:w-12 xl:h-12 rounded-full bg-brand-primary/10 flex items-center justify-center text-brand-primary font-bold text-base xl:text-lg shrink-0 uppercase shadow-sm">
                                {parte.dadosPessoais?.nome?.charAt(0) || "?"}
                              </div>
                              <div className="min-w-0 flex-1">
                                <h3 className="font-semibold text-slate-800 text-sm xl:text-base truncate">
                                  {parte.dadosPessoais?.nome}
                                </h3>
                                <div className="flex flex-wrap gap-2 xl:gap-3 text-xs text-slate-500 font-medium mt-1">
                                  <span className="font-semibold text-slate-600 bg-white px-1.5 py-0.5 rounded border border-slate-200 shadow-sm truncate max-w-[100px] xl:max-w-none">
                                    {parte.dadosPessoais?.pretendenteA || "Não informado"}
                                  </span>
                                  <span className="whitespace-nowrap pt-0.5">CPF: {parte.dadosPessoais?.cpf}</span>
                                  <span className="whitespace-nowrap pt-0.5">Compõe Renda: {parte.perfilFinanceiro?.compoeRenda ? "Sim" : "Não"}</span>
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-200">
                              <span className="text-xs font-semibold flex items-center gap-1 px-2 py-1 rounded-lg text-blue-700 bg-blue-50 border border-blue-100 mr-1 xl:mr-2 whitespace-nowrap shadow-sm">
                                <FileText className="w-3 h-3 xl:w-3.5 xl:h-3.5"/> Docs: {parte.documentos?.length || 0}
                              </span>

                              <button
                                onClick={() => abrirModalDados(parte)}
                                className="p-1.5 xl:p-2 text-slate-400 hover:text-brand-primary hover:bg-brand-primary/10 rounded-lg transition-colors border border-transparent hover:border-brand-primary/20"
                                title="Visualizar Dados Cadastrais"
                              >
                                <Eye className="w-4 h-4 xl:w-5 xl:h-5" />
                              </button>

                              <button
                                onClick={() => abrirModalDocs(parte)}
                                className="px-3 xl:px-4 py-1.5 xl:py-2 bg-white border border-slate-200 rounded-lg text-xs xl:text-sm font-semibold text-brand-primary hover:bg-brand-primary hover:text-white transition-colors shadow-sm whitespace-nowrap"
                              >
                                Analisar Documentos
                              </button>
                            </div>
                          </div>

                          <div className="mt-4 xl:mt-5 pt-3 xl:pt-4 border-t border-slate-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 xl:gap-4 bg-white -mx-4 xl:-mx-5 -mb-4 xl:-mb-5 p-4 xl:p-5">
                            <div className="flex items-center gap-2">
                              <Activity className="w-3.5 h-3.5 xl:w-4 xl:h-4 text-brand-primary" />
                              <span className="text-xs font-semibold text-slate-800 uppercase">
                                Motor de Crédito (FC Análises)
                              </span>
                            </div>

                            <div className="w-full sm:w-auto">
                              {!parteFcData && (
                                <button
                                  onClick={() => executarAnaliseFCIndividual(parte)}
                                  className="w-full sm:w-auto flex items-center justify-center gap-1.5 xl:gap-2 px-3 xl:px-4 py-1.5 xl:py-2 text-xs font-semibold bg-black text-white hover:bg-gray-800 rounded-lg transition-colors shadow-md border border-black"
                                >
                                  <Search className="w-3 h-3 xl:w-3.5 xl:h-3.5" /> Consultar FC
                                </button>
                              )}

                              {parteFcData?.loading && (
                                <div className="flex items-center justify-center gap-1.5 xl:gap-2 px-3 xl:px-4 py-1.5 xl:py-2 text-xs font-semibold text-brand-primary bg-brand-primary/10 rounded-lg border border-brand-primary/20 shadow-sm w-full sm:w-auto">
                                  <Loader2 className="w-3 h-3 xl:w-3.5 xl:h-3.5 animate-spin" /> Conectando ao Bureau...
                                </div>
                              )}

                              {parteFcData?.data && !parteFcData.loading && (
                                <div className="flex flex-wrap items-center justify-between sm:justify-end w-full gap-2 xl:gap-3">

                                  <div className="flex flex-col items-start sm:items-end flex-1 sm:flex-none">
                                    <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded border ${
                                      parteFcData.data.cor === 'green' ? 'bg-green-50 text-green-600 border-green-200' :
                                      parteFcData.data.cor === 'yellow' ? 'bg-yellow-50 text-yellow-600 border-yellow-200' :
                                      'bg-red-50 text-red-600 border-red-200'
                                    }`}>
                                      {parteFcData.data.risco}
                                    </span>
                                    <div className="flex flex-wrap items-center gap-1.5 xl:gap-3 text-[11px] mt-1">
                                      <span className="font-medium text-slate-500">Score: <strong className="text-slate-800">{parteFcData.data.score}</strong></span>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-2 w-full sm:w-auto mt-2 sm:mt-0 shrink-0">
                                    {parteFcData.data.risco === 'EM ANÁLISE' || parteFcData.data.risco === 'PROCESSANDO' || parteFcData.data.risco === 'SOLICITADO' ? (
                                      <div className="flex items-center gap-1.5">
                                        <span className="flex flex-1 sm:flex-none items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-500 bg-slate-100 rounded-lg border border-slate-200 cursor-default shadow-sm w-full sm:w-auto">
                                          <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" /> Processando Laudo...
                                        </span>
                                        <button
                                          onClick={() => atualizarStatusFC(parte.id)}
                                          title="Forçar atualização manual"
                                          className="p-1.5 text-slate-400 hover:text-brand-primary bg-white border border-slate-200 rounded-lg transition-colors shadow-sm"
                                        >
                                          <RefreshCw className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    ) : (
                                      <button
                                        onClick={() => handleVerLaudoPDF(parte.id)}
                                        className={`flex flex-1 sm:flex-none items-center justify-center gap-1 xl:gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors border shadow-sm whitespace-nowrap ${
                                          parteFcData.data.cor === 'green' ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100' :
                                          parteFcData.data.cor === 'yellow' ? 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100' :
                                          'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                                        }`}
                                      >
                                        <ShieldAlert className="w-3.5 h-3.5" /> Ver Relatório
                                      </button>
                                    )}
                                  </div>

                                </div>
                              )}
                            </div>
                          </div>

                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* MODAL DE DADOS CADASTRAIS */}
      {isDataModalOpen && parteDadosSelecionada && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 xl:p-8 animate-fade-in overflow-hidden">
          <div className="absolute inset-0 bg-slate-900 bg-opacity-80 backdrop-blur-sm" onClick={() => setIsDataModalOpen(false)}></div>

          <div className="relative bg-white w-full max-w-4xl flex flex-col rounded-2xl shadow-2xl border border-slate-200 max-h-full overflow-hidden">

            <div className="p-4 xl:p-6 border-b border-slate-200 flex justify-between items-center bg-white shrink-0">
              <div>
                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <User className="w-4 h-4 xl:w-5 xl:h-5 text-brand-primary"/> Dados Cadastrais
                </h2>
                <p className="text-sm text-slate-500 font-medium mt-1">{parteDadosSelecionada.dadosPessoais?.nome}</p>
              </div>
              <button onClick={() => setIsDataModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1.5 xl:p-2 bg-slate-50 border border-slate-200 rounded-full transition-colors">
                <X className="w-4 h-4 xl:w-5 xl:h-5"/>
              </button>
            </div>

            <div className="p-4 xl:p-6 overflow-y-auto flex-1 min-h-0 space-y-3 xl:space-y-4 bg-slate-50 custom-scrollbar">

              <details className="group" open>
                <summary className="flex justify-between items-center font-bold cursor-pointer list-none p-3 xl:p-4 bg-white rounded-xl border border-slate-200 shadow-sm select-none hover:border-brand-primary/50 transition-colors [&::-webkit-details-marker]:hidden">
                  <span className="flex items-center gap-2 text-sm text-slate-800 font-semibold">
                    <Contact className="w-4 h-4 text-brand-primary"/> 1. Dados Pessoais
                  </span>
                  <span className="transition-transform duration-200 group-open:rotate-90">
                    <ChevronRight className="w-4 h-4 xl:w-5 xl:h-5 text-slate-400" />
                  </span>
                </summary>
                <div className="mt-2 xl:mt-3 p-4 xl:p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 xl:gap-6 bg-white rounded-xl border border-slate-200 shadow-sm">
                  <DataField label="CPF" value={parteDadosSelecionada.dadosPessoais?.cpf} />
                  <DataField label="Nome Completo" value={parteDadosSelecionada.dadosPessoais?.nome} />
                  <DataField label="Doc. Complementar" value={`${parteDadosSelecionada.dadosPessoais?.docComplementar || ""} ${parteDadosSelecionada.dadosPessoais?.numDocComplementar || ""}`} />

                  <DataField label="Data de Nascimento" value={parteDadosSelecionada.dadosPessoais?.dataNascimento} />
                  <DataField label="Profissão" value={parteDadosSelecionada.dadosPessoais?.profissao} />
                  <DataField label="Estado Civil" value={parteDadosSelecionada.dadosPessoais?.estadoCivil} />

                  <DataField label="Nacionalidade" value={parteDadosSelecionada.dadosPessoais?.nacionalidade} />
                  <DataField label="Pretendente à" value={parteDadosSelecionada.dadosPessoais?.pretendenteA} />
                  <DataField label="Vai morar no imóvel?" value={parteDadosSelecionada.dadosPessoais?.morarImovel ? "Sim" : "Não"} />

                  <DataField label="E-mail" value={parteDadosSelecionada.dadosPessoais?.email} />
                  <DataField label="Telefone" value={parteDadosSelecionada.dadosPessoais?.telefone} />
                  <div className="md:col-span-2 lg:col-span-3">
                    <DataField label="Observações da Parte" value={parteDadosSelecionada.dadosPessoais?.observacoes} />
                  </div>
                </div>
              </details>

              <details className="group">
                <summary className="flex justify-between items-center font-bold cursor-pointer list-none p-3 xl:p-4 bg-white rounded-xl border border-slate-200 shadow-sm select-none hover:border-brand-primary/50 transition-colors [&::-webkit-details-marker]:hidden">
                  <span className="flex items-center gap-2 text-sm text-slate-800 font-semibold">
                    <DollarSign className="w-4 h-4 text-brand-primary"/> 2. Perfil Financeiro
                  </span>
                  <span className="transition-transform duration-200 group-open:rotate-90">
                    <ChevronRight className="w-4 h-4 xl:w-5 xl:h-5 text-slate-400" />
                  </span>
                </summary>
                <div className="mt-2 xl:mt-3 p-4 xl:p-5 bg-white rounded-xl border border-slate-200 shadow-sm">
                  <div className="mb-4 flex items-center gap-2">
                    <span className="text-xs font-medium text-slate-500">Compõe Renda? </span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded shadow-sm ${parteDadosSelecionada.perfilFinanceiro?.compoeRenda ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-slate-100 text-slate-500 border border-slate-200'}`}>
                      {parteDadosSelecionada.perfilFinanceiro?.compoeRenda ? "SIM" : "NÃO"}
                    </span>
                  </div>

                  {parteDadosSelecionada.perfilFinanceiro?.compoeRenda && (
                    <div className="space-y-3">
                      {parteDadosSelecionada.perfilFinanceiro?.rendas?.map((renda, idx) => (
                        <div key={idx} className="grid grid-cols-1 sm:grid-cols-3 gap-3 xl:gap-4 bg-slate-50 p-3 xl:p-4 rounded-xl border border-slate-200">
                          <DataField label="Perfil Por" value={renda.perfilPor} />
                          <DataField label="Origem da Renda" value={renda.origemRenda} />
                          <DataField label="Valor" value={`R$ ${renda.valor || "0,00"}`} className="text-slate-800 font-bold" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </details>

              <details className="group">
                <summary className="flex justify-between items-center font-bold cursor-pointer list-none p-3 xl:p-4 bg-white rounded-xl border border-slate-200 shadow-sm select-none hover:border-brand-primary/50 transition-colors [&::-webkit-details-marker]:hidden">
                  <span className="flex items-center gap-2 text-sm text-slate-800 font-semibold">
                    <Home className="w-4 h-4 text-brand-primary"/> 3. Endereço Cadastrado
                  </span>
                  <span className="transition-transform duration-200 group-open:rotate-90">
                    <ChevronRight className="w-4 h-4 xl:w-5 xl:h-5 text-slate-400" />
                  </span>
                </summary>
                <div className="mt-2 xl:mt-3 p-4 xl:p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 xl:gap-6 bg-white rounded-xl border border-slate-200 shadow-sm">
                  <DataField label="CEP" value={parteDadosSelecionada.endereco?.cep} />
                  <DataField label="Endereço" value={parteDadosSelecionada.endereco?.endereco} className="sm:col-span-2 lg:col-span-2" />

                  <DataField label="Número" value={parteDadosSelecionada.endereco?.numero} />
                  <DataField label="Complemento" value={parteDadosSelecionada.endereco?.complemento} />
                  <DataField label="Bairro" value={parteDadosSelecionada.endereco?.bairro} />

                  <DataField label="Cidade" value={parteDadosSelecionada.endereco?.cidade} className="sm:col-span-2 lg:col-span-2" />
                  <DataField label="Estado (UF)" value={parteDadosSelecionada.endereco?.estado} />
                </div>
              </details>

            </div>

            <div className="p-4 xl:p-6 border-t border-slate-200 bg-white flex justify-end shrink-0">
              <button
                onClick={() => setIsDataModalOpen(false)}
                className="px-5 xl:px-6 py-2 xl:py-2.5 text-sm font-semibold bg-brand-primary text-white rounded-full transition-colors shadow-md hover:bg-teal-600"
              >
                Fechar Visualização
              </button>
            </div>

          </div>
        </div>
      )}

      {/* MODAL DE PARECER */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 xl:p-8 animate-fade-in overflow-hidden">
          <div className="absolute inset-0 bg-slate-900 bg-opacity-80 backdrop-blur-sm" onClick={() => setIsModalOpen(false)}></div>
          <div className="relative bg-white w-full max-w-lg flex flex-col rounded-2xl shadow-2xl overflow-hidden border border-slate-200 max-h-full">
            <div className={`p-4 xl:p-6 border-b border-slate-200 flex justify-between items-center ${parecerType === 'Rejeitada' || parecerType === 'Reprovar Contrato' ? 'bg-red-50' : 'bg-yellow-50'} shrink-0`}>
              <h2 className={`text-lg font-bold flex items-center gap-2 ${parecerType === 'Rejeitada' || parecerType === 'Reprovar Contrato' ? 'text-red-700' : 'text-yellow-700'}`}>
                {parecerType === 'Rejeitada' || parecerType === 'Reprovar Contrato' ? <XCircle className="w-5 h-5 xl:w-6 xl:h-6"/> : <AlertCircle className="w-5 h-5 xl:w-6 xl:h-6"/>}
                {parecerType === 'Rejeitada' ? 'Motivo da Reprovação' : parecerType === 'Reprovar Contrato' ? 'Motivo da Recusa das Assinaturas' : 'Detalhar Pendência'}
              </h2>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1.5 xl:p-2 bg-white border border-slate-200 rounded-full transition-colors shadow-sm"><X className="w-4 h-4 xl:w-5 xl:h-5"/></button>
            </div>

            <div className="p-4 xl:p-6 flex-1 min-h-0 overflow-y-auto bg-slate-50 custom-scrollbar flex flex-col gap-4">

              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                <label className="flex items-center gap-2 text-xs font-semibold text-brand-primary mb-2">
                  <Zap className="w-3.5 h-3.5 xl:w-4 xl:h-4"/> Usar Resposta Rápida (Opcional)
                </label>
                <select
                  className="w-full px-3 py-2 xl:py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 focus:outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary transition-colors cursor-pointer"
                  onChange={handleSelectModelo}
                  defaultValue=""
                >
                  <option value="" disabled>-- Selecione um modelo --</option>
                  {modelosParecer.map((modelo) => (
                    <option key={modelo.id} value={modelo.id}>
                      {modelo.nome} {modelo.titulo ? `(${modelo.titulo})` : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs font-medium text-slate-400 mt-2">Ao selecionar, o texto abaixo será substituído.</p>
              </div>

              <div className="flex-1 flex flex-col">
                <label className="block text-xs font-semibold text-slate-500 mb-2">
                  Mensagem para a Imobiliária
                </label>
                <textarea
                  value={parecerText}
                  onChange={(e) => setParecerText(e.target.value)}
                  placeholder="Digite o parecer técnico..."
                  className="w-full h-28 xl:h-32 p-3 xl:p-4 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary resize-none text-slate-800 font-medium transition-colors shadow-inner"
                ></textarea>
              </div>

            </div>

            <div className="p-4 xl:p-6 border-t border-slate-200 bg-white flex justify-end gap-2 xl:gap-3 shrink-0">
              <button onClick={() => setIsModalOpen(false)} className="px-5 xl:px-6 py-2 xl:py-2.5 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-full transition-colors border border-slate-200 shadow-sm">
                Cancelar
              </button>
              <button onClick={confirmarParecer} className={`px-5 xl:px-6 py-2 xl:py-2.5 text-sm font-semibold text-white rounded-full transition-colors shadow-md ${parecerType === 'Rejeitada' || parecerType === 'Reprovar Contrato' ? 'bg-red-500 hover:bg-red-600 shadow-red-500/30' : 'bg-yellow-500 hover:bg-yellow-600 shadow-yellow-500/30'}`}>
                Confirmar {parecerType === 'Rejeitada' ? 'Reprovação' : parecerType === 'Reprovar Contrato' ? 'Recusa' : 'Pendência'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE DOCUMENTOS */}
      {isDocModalOpen && parteSelecionada && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 xl:p-8 animate-fade-in overflow-hidden">
          <div className="absolute inset-0 bg-slate-900 bg-opacity-80 backdrop-blur-sm" onClick={() => setIsDocModalOpen(false)}></div>
          <div className="relative bg-white w-full max-w-3xl flex flex-col rounded-2xl shadow-2xl overflow-hidden border border-slate-200 max-h-full">
            <div className="p-4 xl:p-6 border-b border-slate-200 flex justify-between items-center bg-white shrink-0">
              <div>
                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <FileText className="w-4 h-4 xl:w-5 xl:h-5 text-brand-primary"/> Documentos Anexados
                </h2>
                <p className="text-sm text-slate-500 font-medium mt-1">{parteSelecionada.dadosPessoais?.nome}</p>
              </div>
              <button onClick={() => setIsDocModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1.5 xl:p-2 bg-slate-50 border border-slate-200 rounded-full transition-colors shadow-sm"><X className="w-4 h-4 xl:w-5 xl:h-5"/></button>
            </div>

            <div className="p-4 xl:p-6 overflow-y-auto min-h-0 flex-1 space-y-3 xl:space-y-4 bg-slate-50 custom-scrollbar">
              {!parteSelecionada.documentos || parteSelecionada.documentos.length === 0 ? (
                <div className="py-10 text-center text-slate-400 font-medium border-2 border-dashed border-slate-200 rounded-xl bg-white text-sm">
                  Nenhum arquivo enviado para esta parte.
                </div>
              ) : (
                parteSelecionada.documentos.map((docItem, idx) => {
                  const uniqueDocId = docItem.id || `${parteSelecionada.id}-${idx}`;
                  const currentStatus = docValidations[uniqueDocId] || null;

                  return (
                    <div key={idx} className={`flex flex-col p-3 xl:p-4 border rounded-xl gap-3 shadow-sm transition-colors ${currentStatus === 'rejected' ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-white hover:border-brand-primary/50'}`}>
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 xl:gap-4">
                        <div className="w-full sm:w-auto">
                          <p className="font-semibold text-sm text-slate-800 flex items-center gap-2 truncate">
                            {docItem.nome}
                          </p>
                          <p className="text-xs text-slate-500 mt-1 uppercase font-medium">{docItem.tipo}</p>
                          {docItem.obs && <p className="text-xs text-slate-600 font-medium italic mt-2 border-l-2 border-brand-primary pl-2 line-clamp-2">Obs: {docItem.obs}</p>}
                        </div>

                        <div className="flex items-center gap-2 w-full sm:w-auto justify-end mt-2 sm:mt-0 pt-2 sm:pt-0 border-t border-slate-100 sm:border-0">
                          <div className="flex bg-slate-50 rounded-lg p-1 mr-1 xl:mr-2 border border-slate-200 shadow-inner">
                            <button
                              title="Status retornado via FC Análises"
                              type="button"
                              className={`p-1 xl:p-1.5 rounded transition-colors cursor-default ${currentStatus === 'approved' ? 'bg-white text-green-600 shadow-sm border border-green-100' : 'text-slate-300'}`}
                            >
                              <CheckCircle className="w-3.5 h-3.5 xl:w-4 xl:h-4" />
                            </button>
                            <button
                              title="Status retornado via FC Análises"
                              type="button"
                              className={`p-1 xl:p-1.5 rounded transition-colors cursor-default ${currentStatus === 'rejected' ? 'bg-white text-red-600 shadow-sm border border-red-100' : 'text-slate-300'}`}
                            >
                              <XCircle className="w-3.5 h-3.5 xl:w-4 xl:h-4" />
                            </button>
                          </div>

                          <a
                            href={docItem.url}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center justify-center gap-1.5 xl:gap-2 px-3 xl:px-4 py-1.5 xl:py-2 bg-brand-primary/10 text-brand-primary hover:bg-brand-primary hover:text-white rounded-lg transition-colors font-semibold text-xs shrink-0 shadow-sm"
                          >
                            Abrir PDF <ExternalLink className="w-3 h-3 xl:w-3.5 xl:h-3.5"/>
                          </a>
                        </div>
                      </div>

                      {currentStatus === 'rejected' && (
                        <div className="w-full mt-1 xl:mt-2 animate-fade-in">
                          <input
                            type="text"
                            disabled
                            value="FC Análises: Documento divergente ou ilegível."
                            className="w-full bg-red-100 border border-red-200 rounded-lg px-3 py-1.5 xl:py-2 text-xs focus:outline-none font-medium text-red-700 cursor-not-allowed opacity-90 shadow-inner"
                          />
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            <div className="p-4 xl:p-6 border-t border-slate-200 bg-white flex justify-between items-center shrink-0">
              {parteSelecionada.documentos && parteSelecionada.documentos.length > 0 ? (
                <button
                  onClick={handleAbrirTodosDocs}
                  className="px-3 xl:px-4 py-2 xl:py-2.5 text-sm font-semibold text-teal-700 bg-teal-50 border border-teal-200 hover:bg-teal-100 rounded-full flex items-center gap-1.5 xl:gap-2 transition-colors shadow-sm"
                >
                  <Layers className="w-3.5 h-3.5 xl:w-4 xl:h-4"/> Abrir Todos
                </button>
              ) : (
                <div></div>
              )}

              <button
                onClick={() => setIsDocModalOpen(false)}
                className="px-5 xl:px-6 py-2 xl:py-2.5 text-sm font-semibold bg-brand-primary text-white rounded-full transition-colors shadow-md shadow-brand-primary/30 hover:bg-teal-600"
              >
                Fechar Análise
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE DOCUMENTOS ASSINADOS (CLICKSIGN) */}
      {isSignedDocsModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 xl:p-8 animate-fade-in overflow-hidden">
          <div className="absolute inset-0 bg-slate-900 bg-opacity-80 backdrop-blur-sm" onClick={() => setIsSignedDocsModalOpen(false)}></div>
          <div className="relative bg-white w-full max-w-2xl flex flex-col rounded-2xl shadow-2xl overflow-hidden border border-slate-200 max-h-full">
            <div className="p-4 xl:p-6 border-b border-slate-200 flex justify-between items-center bg-white shrink-0">
              <div>
                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <FileSignature className="w-5 h-5 text-brand-primary"/> Documentos Assinados
                </h2>
                <p className="text-sm text-slate-500 font-medium mt-1">Arquivos da Imobiliária e Certificados ClickSign.</p>
              </div>
              <button onClick={() => setIsSignedDocsModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1.5 xl:p-2 bg-slate-50 border border-slate-200 rounded-full transition-colors shadow-sm"><X className="w-5 h-5"/></button>
            </div>

            <div className="p-4 xl:p-6 overflow-y-auto min-h-0 flex-1 space-y-3 xl:space-y-4 bg-slate-50 custom-scrollbar">
              {getSignedDocuments().length === 0 ? (
                <div className="py-10 text-center text-slate-400 font-medium border-2 border-dashed border-slate-200 rounded-xl bg-white text-sm">
                  Nenhum arquivo encontrado para esta solicitação.
                </div>
              ) : (
                getSignedDocuments().map((docItem, idx) => (
                  <div key={idx} className="flex flex-col sm:flex-row p-3 xl:p-4 border border-slate-200 rounded-xl bg-white hover:border-brand-primary/50 transition-colors shadow-sm justify-between items-start sm:items-center gap-3">
                    <div className="flex items-center gap-3 w-full sm:w-auto overflow-hidden">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${docItem.isFisico ? 'bg-orange-100 text-orange-600' : 'bg-brand-primary/10 text-brand-primary'}`}>
                        <FileText className="w-5 h-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm text-slate-800 truncate" title={docItem.nome}>
                          {docItem.nome}
                        </p>
                        <p className={`text-xs font-medium mt-0.5 ${docItem.isFisico ? 'text-orange-600' : 'text-slate-500'}`}>
                          {docItem.isFisico ? 'Upload Físico - Imobiliária' : 'Autenticado via ClickSign'}
                        </p>
                      </div>
                    </div>

                    <div className="w-full sm:w-auto flex justify-end mt-2 sm:mt-0">
                        <button
                          onClick={() => window.open(docItem.url, '_blank', 'noopener,noreferrer')}
                          className="flex items-center justify-center gap-1.5 px-4 py-2 bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 hover:text-blue-800 rounded-lg transition-colors font-semibold text-xs shadow-sm w-full sm:w-auto whitespace-nowrap"
                        >
                          Abrir PDF <ExternalLink className="w-3.5 h-3.5"/>
                        </button>
                      </div>
                  </div>
                ))
              )}
            </div>

            <div className="p-4 xl:p-6 border-t border-slate-200 bg-white flex justify-end shrink-0">
              <button
                onClick={() => setIsSignedDocsModalOpen(false)}
                className="px-6 py-2.5 text-sm font-semibold bg-brand-primary text-white rounded-full transition-colors shadow-md shadow-brand-primary/30 hover:bg-teal-600"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminAnaliseSolicitacaoPage;